import { createAutoTask } from "./taskService";
import { createLegalWorkLog, getModuleLabel } from "./legalWorkLogService";
import { logActivity } from "./activityLog"; // Import Backend Logger
import type { WorkLogModule } from "../types/legalWorkLog";

export type LegalActivityParams = {
  sourceType: string; // e.g., "contract", "seal", "govCorrespondence"
  sourceId: string;
  title: string;
  
  // Actor info
  actorUserId: string;
  actorUserName: string;
  
  // Context
  description?: string;
  counterpartOrDept?: string;
  
  // Options
  todoRequired?: boolean; // If true, creates a Pending Task
  logOnly?: boolean;      // If true, only writes to Work Log (History), no Task
  
  // Overrides
  actionLabel?: string;   // Custom action name e.g. "啟用範本"
  assigneeId?: string;    // For tasks
  assigneeName?: string;  // For tasks
  priority?: "High" | "Medium" | "Low";
};

// Helper to map UI module keys to Service Module Enums
function mapToWorkLogModule(m: string): WorkLogModule {
  const map: Record<string, WorkLogModule> = {
    contract: "CONTRACT_SYSTEM",
    contractDraft: "CONTRACT_DRAFT",
    case: "DISPUTE", 
    legalMatter: "DISPUTE",
    ipMatter: "IP_PROTECTION",
    ip: "IP_PROTECTION",
    brandAsset: "BRAND",
    sealRequest: "STAMP",
    sealApplication: "STAMP",
    govCorrespondence: "PUBLIC_DOC",
    govInbound: "PUBLIC_DOC",
    governanceDoc: "GOVERNANCE",
    govCompliance: "GOVERNANCE",
    govProject: "GOVERNANCE",
    template: "TEMPLATE_CENTER",
    other: "OTHER"
  };
  return map[m] || "OTHER";
}

// Helper: Map sourceType to simple module name for Audit Log
function mapToAuditModule(sourceType: string): string {
  if (sourceType === 'sealRequest' || sourceType === 'sealApplication') return 'seal';
  if (sourceType === 'contractDraft') return 'contractReview';
  if (sourceType === 'govCorrespondence') return 'govCorrespondence';
  return sourceType; // Default pass-through
}

export async function createLegalActivity(params: LegalActivityParams) {
  try {
    const moduleEnum = mapToWorkLogModule(params.sourceType);
    const auditModule = mapToAuditModule(params.sourceType);
    
    // 1. If Task is required (Pending Item)
    if (params.todoRequired) {
      await createAutoTask({
        title: params.title,
        description: params.description,
        module: params.sourceType,
        refId: params.sourceId,
        assigneeName: params.assigneeName,
        assigneeId: params.assigneeId
      });
    }

    // 2. Work Log (Frontend Display History)
    await createLegalWorkLog({
      userId: params.actorUserId,
      userName: params.actorUserName,
      module: moduleEnum,
      recordType: 'log',
      action: params.actionLabel || (params.todoRequired ? 'SUBMITTED' : 'CREATED'),
      status: 'done',
      finalStatus: '已紀錄',
      refId: params.sourceId,
      title: params.title,
      summary: params.description || `新增/更新項目 (${getModuleLabel(moduleEnum)})`,
      description: params.description,
      counterpartOrDept: params.counterpartOrDept
    });

    // 3. Audit Log (Backend Secure Log with IP)
    // This ensures every "Legal Activity" is recorded in the system audit trail.
    await logActivity({
      module: auditModule as any,
      action: params.actionLabel || "update",
      entityId: params.sourceId,
      entityType: params.sourceType,
      entityTitle: params.title,
      actorId: params.actorUserId,
      actorName: params.actorUserName,
      details: params.description || params.title
    });

    console.log("[LegalActivity] Processed:", params.title);
  } catch (e) {
    console.error("[LegalActivity] Failed:", e);
  }
}
